<script>
  import { onMount, onDestroy } from 'svelte';
  import gsap from 'gsap';
  import { ScrollTrigger } from 'gsap/ScrollTrigger';
  
  export let groupId;
  export let backgroundColors;
  export let onBackgroundChange;
  
  let groupElement;
  let panels;
  let contentBoxes;
  let mediaElements;
  
  onMount(() => {
    // Only clear ScrollTriggers that belong to this group, not all triggers
    ScrollTrigger.getAll().forEach(st => {
      if (st.vars && st.vars.id) {
        const triggerId = st.vars.id;
        if (triggerId === groupId || 
            triggerId.startsWith(`content-${groupId}`) ||
            triggerId.startsWith(`media-${groupId}`) ||
            triggerId.startsWith(`bg-${groupId}`)) {
          st.kill();
        }
      }
    });
    
    // Get elements within this group
    panels = groupElement.querySelectorAll('.panel');
    contentBoxes = groupElement.querySelectorAll('.content-box');
    mediaElements = groupElement.querySelectorAll('.media-element');
    
    // Set all content boxes to be visible by default
    gsap.set(contentBoxes, { opacity: 1 });
    
    // Create animations for this panel group
    let groupTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: groupElement,
        start: "top bottom",
        end: "bottom top",
        scrub: 1,
        markers: false, // Set to true for debugging
        id: groupId
      }
    });
    
    // Setup background transitions
    setupBackgroundTransitions();
    
    // Setup content animations
    setupContentAnimations();
    
    // Setup media animations
    setupMediaAnimations();
  });
  
  onDestroy(() => {
    // Clean up ScrollTrigger instances for this group
    ScrollTrigger.getAll().forEach(st => {
      if (st.vars.id === groupId) {
        st.kill();
      }
    });
  });
  
  function setupBackgroundTransitions() {
    const groupIndex = parseInt(groupId.replace('group', '')) - 1;
    if (groupIndex >= 0 && groupIndex < backgroundColors.length) {
      const colorClass = backgroundColors[groupIndex];
      
      ScrollTrigger.create({
        trigger: groupElement,
        start: "top center",
        end: "bottom center",
        onEnter: () => {
          onBackgroundChange(colorClass);
        },
        onEnterBack: () => {
          onBackgroundChange(colorClass);
        },
        id: `bg-${groupId}`
      });
    }
  }
  
  function setupContentAnimations() {
    contentBoxes.forEach((box, index) => {
      // Set initial state - make content boxes visible by default
      gsap.set(box, {
        opacity: 1,
        y: 0,
        scale: 1
      });
      
      // Create subtle animation - content boxes are always visible
      ScrollTrigger.create({
        trigger: box,
        start: "top 80%",
        end: "bottom 20%",
        onEnter: () => {
          gsap.fromTo(box, {
            opacity: 0.7,
            y: 20,
            scale: 0.98
          }, {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.6,
            ease: "power2.out"
          });
        },
        onLeave: () => {
          gsap.to(box, {
            opacity: 0.8,
            y: -10,
            scale: 0.99,
            duration: 0.3,
            ease: "power2.inOut"
          });
        },
        onEnterBack: () => {
          gsap.fromTo(box, {
            opacity: 0.7,
            y: 20,
            scale: 0.98
          }, {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.6,
            ease: "power2.out"
          });
        },
        onLeaveBack: () => {
          gsap.to(box, {
            opacity: 0.7,
            y: 20,
            scale: 0.98,
            duration: 0.3,
            ease: "power2.inOut"
          });
        },
        id: `content-${groupId}-${index}`
      });
    });
  }
  
  function setupMediaAnimations() {
    mediaElements.forEach((media, index) => {
      // Set initial state
      gsap.set(media, {
        scale: 1.1,
        opacity: 0
      });
      
      // Create animation
      ScrollTrigger.create({
        trigger: media,
        start: "top 70%",
        end: "bottom 30%",
        onEnter: () => {
          gsap.to(media, {
            scale: 1,
            opacity: 1,
            duration: 1.2,
            ease: "power2.out"
          });
        },
        onLeave: () => {
          gsap.to(media, {
            scale: 0.95,
            opacity: 0.8,
            duration: 0.6,
            ease: "power2.inOut"
          });
        },
        onEnterBack: () => {
          gsap.to(media, {
            scale: 1,
            opacity: 1,
            duration: 1.2,
            ease: "power2.out"
          });
        },
        onLeaveBack: () => {
          gsap.to(media, {
            scale: 1.1,
            opacity: 0,
            duration: 0.6,
            ease: "power2.inOut"
          });
        },
        id: `media-${groupId}-${index}`
      });
    });
  }
</script>

<div class="panel-group" id={groupId} bind:this={groupElement}>
  <slot />
</div>

<style>
  .panel-group {
    position: relative;
    width: 100%;
  }
</style>

